import { IonItem } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { type Project } from "@/lib/backend/api";
import { queryOptions_signed_key_project } from "@/lib/requests/file/file.get-signed-key";
import { Routes } from "@/lib/router";

type ProjectCardFeaturedProps = {
  project: Project;
};

export function ProjectCardFeatured({ project }: ProjectCardFeaturedProps) {
  const myQueryOptions = queryOptions_signed_key_project([project.id, project.image ?? ""]);
  const { data: src } = useQuery(myQueryOptions);

  const userFunctions = project.stats?.uniqueUserFunctions?.join(", ") ?? "";

  const thisYear = new Date().getFullYear();
  const startDate = project?.startDate ? new Date(project.startDate) : undefined;
  const endDate = project?.endDate ? new Date(project.endDate) : undefined;
  const isStartAndEndDateBothThisYear =
    startDate?.getFullYear() === thisYear && endDate?.getFullYear() === thisYear;

  // Check if start and end dates are the same day
  const isSameDay =
    startDate &&
    endDate &&
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  const dateDisplay = () => {
    if (startDate && endDate && !isSameDay) {
      // Short format for both dates when they're different
      return `${startDate.toLocaleDateString("nl-NL", {
        month: "short",
        day: "numeric",
      })} - ${endDate.toLocaleDateString("nl-NL", {
        month: "short",
        day: "numeric",
      })}`;
    }
    // Single date or same day - full format
    return startDate?.toLocaleDateString("nl-NL", {
      year: isStartAndEndDateBothThisYear ? undefined : "numeric",
      month: "long",
      day: "numeric",
    });
  };
  // const searchParams = { fromUrl: Routes.ProjectInfo({ id }) };

  return (
    <IonItem
      button
      detail={false}
      routerLink={Routes.ProjectOverview(
        { id: project.id },
        {
          search: {
            fromUrl: Routes.ProjectShifts({ id: project.id }),
          },
        },
      )}
      routerDirection="forward"
      className="ion-no-padding w-full [--border-width:0] [--inner-padding-end:0] [--padding-end:0] [--padding-start:0]"
      lines="none"
    >
      <Card className="w-full overflow-hidden border-none bg-surface-primary shadow-none">
        <div className="relative h-20 w-full">
          <img
            src={src?.url}
            alt={project.name}
            className="h-full w-full text-ellipsis whitespace-nowrap object-cover"
          />
        </div>
        <CardContent className="p-3">
          <div className="flex flex-col gap-1">
            <h3 className="truncate text-sm font-bold text-txt-primary">{project.name}</h3>
            <p className="text-brand overflow-hidden text-ellipsis whitespace-nowrap text-xsm font-bold text-txt-brand">
              {userFunctions}
            </p>

            <div className="flex items-center justify-between text-sm font-medium text-txt-primary">
              {startDate && <span>{dateDisplay()}</span>}
            </div>
          </div>
        </CardContent>
      </Card>
    </IonItem>
  );
}
